import moment from "moment";
import "moment-timezone";
import translate from "../../../../i18n/translate";
import { License, SessionStatus, TMSGetCourse } from "../ProgressDetailsInterfaces";
import allActions from "../../../../redux/actions";

export const getDateAndTimeFormat = (timestamp: number) => {
  return moment.unix(timestamp).tz(moment.tz.guess(true)).format("DD MMMM YYYY | hh:mm A z");
};

const status = {
  upcoming: 0,
  scheduled: 1,
  completed: 2,
  absent: 3,
  scheduling: 4,
};


export const getSessionStatus = (session: SessionStatus) => {
  if (session?.Status === status.upcoming) {
    return translate("progress_subskill_sessionStatusUpcoming");
  } else if (session?.Status === status.scheduled) {
    return translate("progress_subskill_sessionStatusScheduled");
  } else if (session?.Status === status.completed) {
    return translate("progress_subskill_sessionStatusCompleted");
  } else if (session?.Status === status.absent) {
    return translate("progress_subskill_sessionStatusAbsent");
  } else if (session?.Status === status.scheduling) {
    return translate("progress_subskill_sessionStatusScheduling");
  } else {
    return translate("progress_subskill_sessionStatusNotCompleted");
  }
};

export const getFirstLettersLowerCase = (inputString: string): string => {
  const wordsArray = inputString.split(" ");
  let firstLetters = "";

  for (const word of wordsArray) {
    if (word.length > 0) {
      firstLetters += word[0].toLowerCase();
    }
  }
  return firstLetters;
};

export const findIndexByModuleId = (data: any, moduleId: any) => {
  for (let i = 0; i < data.length; i++) {
    if (data[i].id === moduleId) {
      return i;
    }
  }
  return -1; // Return -1 if not found
}

export const fetchCertificateFromURL = (dispatch: any, setDownloadIconLoader: any, props: any) => {
  const downloadCertificate: any = process.env.REACT_APP_FORGOTEMAILURL + "/getcertificate" || "";
  const downloadCertificateKey: any = process.env.REACT_APP_XAPIKEY ?? "";

  let url = `${downloadCertificate}?userId=${props.certificateData.UserId || ""}&courseId=${props.certificateData.CourseId || ""}&licenseId=${props.certificateData.LicenseId || ""}&part=${props.activeLevel.id}&productId=${parseInt(props.certificateData.productId)}`;
  const headers = {
    "x-api-key": downloadCertificateKey ?? "",
  };

  fetch(url, { headers })
    .then((res) => {
      if (!res.ok) {
        throw new Error('Network response was not ok');
      }
      return res.blob();
    })
    .then((blob) => {
      const data = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = data;
      a.download = 'Certificate.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      dispatch(allActions.toasterActions.showToastMessage(true, 'success', 'progress_certificate_download_success'));
      setDownloadIconLoader(false);
    })
    .catch((error) => {
      dispatch(allActions.toasterActions.showToastMessage(true, 'error', 'message_errorDefault'));
      setDownloadIconLoader(false);
    });
}

export const getLatestCompletedCourseId = (latestCompleted: TMSGetCourse[]) => {
  const sortedCompleted = [...latestCompleted].sort(
    (a: { courseId: number }, b: { courseId: number }) => b?.courseId - a?.courseId
  );
  return sortedCompleted[0]?.courseId;
};

export const getCourseId = (activeCourse: string | any[], tmsGetCourse: TMSGetCourse[]) => {
  if (activeCourse.length > 0) {
    return activeCourse[0].courseId;
  } else {
    const latestCompleted = tmsGetCourse.filter((courseFilter: { status: string }) => courseFilter.status === "5");
    return latestCompleted.length > 0 ? getLatestCompletedCourseId(latestCompleted) : 0;
  }
};

export const dispatchUserProgressDetails = (allInfo: any, courseDetailsData: any, subValue: any, dispatch: any, levelList: any, setCourseData: any) => {
  const licenses = allInfo.peopleInformation.data.getpeopleInformation.license;
  if (licenses.length) {
    const sprintLicenses = licenses.filter((prod: { productId: string }) =>
      (["20", "18", "21", "22", "23", "24", "25", "29"].includes(prod.productId))
    )
    setCourseData({
      UserId: courseDetailsData.UserId,
      LicenseId: courseDetailsData.LicenseId,
      CourseId: courseDetailsData.CourseId,
      Level: courseDetailsData.Level,
      productId: sprintLicenses[0]?.productId
    })
    const product = licenses.find((e: License) => e.productId == "18" || e.productId == "29");
    const moduleId = levelList[subValue]?.id;
    if (product && moduleId) {
      dispatch(
        allActions.progressDetailsAction.getMyProgressDetailsApiCall(
          courseDetailsData.UserId,
          courseDetailsData.CourseId,
          product.productId,
          moduleId
        )
      );
    }
  }
}

export const dispatchActiveCourseDetails = (shouldDispatchAction: any, allInfo: any, getCoursesByStatus: any, filteredSprintLevelLic: any, dispatch: any) => {
  if (shouldDispatchAction) {
    const uuId = allInfo.common.luuId;
    const activeCourse = getCoursesByStatus(allInfo.tmsCourses.data.getTMSGetCourse);
    const courseId = getCourseId(activeCourse, allInfo.tmsCourses.data.getTMSGetCourse);

    if (filteredSprintLevelLic.length > 0) {
      dispatch(
        allActions.currentCourseDetailsAction.getStoreActiveCourseInfo(
          allInfo.common.leuId,
          filteredSprintLevelLic[0].licenseId,
          Number(filteredSprintLevelLic[0].productId),
          uuId,
          courseId
        )
      );
    }
  }

  if (allInfo.currentCourseDetails.isFailed) {
    dispatch(allActions.toasterActions.showToastMessage(true, "error", "message_errorDefault"));
  }
}

export const setCurrentCourseTab = (allInfo: any, levelData: any, setSubValue: any, setLoaderFlag: any) => {
  const completedLevel = allInfo.currentCourseDetails.data?.Completed?.skill;
  if (completedLevel?.length > 0) {
    const currentTab = completedLevel[completedLevel.length - 1];
    if (currentTab?.ModuleId) {
      const tabIndex = findIndexByModuleId(levelData, currentTab?.ModuleId);
      setSubValue(tabIndex);
    }
  }
  setLoaderFlag(false);
}

export const getStudentStatus = (studentStatus: string) => {
  if (studentStatus === 'missed' || studentStatus === 'canceled_late') {
    return 'lost';
  } else if (studentStatus === 'participated') {
    return 'finished';
  } else {
    return studentStatus;
  }
}

type EmailFeatureSetting = {
  emailFeatureName: string;
  status: number;
};

type OrganizationDetail = {
  emailFeatureSettings: EmailFeatureSetting[];
};

export type TLicense = {
  productId: string;
  productName: string;
  isDemoLicense: null | boolean;
  organizationDetail: OrganizationDetail | null;
  licenseId: string;
  startDate: string;
  endDate: string;
  active: string;
};

export const getProgressProductId = (licenseList: Array<TLicense>, filterProducts: Array<string>) => {
  return licenseList.find((license: TLicense) => filterProducts.includes(license.productId))?.productId;
};